import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { formatCount, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * What was read from the file, across the top of the step.
 *
 * The same strip the Tab Service portal puts above its Load Data step, and the same five
 * facts. They are the ones somebody checks against their own system before they let anything
 * go to the IRS: is this the file I meant, how many rows did it have, and how many of them
 * came through. Buried in a sentence they get skimmed; in a row of labelled figures they get
 * read.
 */
export function FileSummaryStrip({
  fileName,
  startedAt,
  total,
  succeeded,
  failed,
}: {
  fileName: string;
  /**
   * When this upload was started.
   *
   * Not "when the file was checked": nothing records that, and the portal's own strip shows a
   * dash in that column rather than inventing one. This is a time Tab Service actually holds,
   * so it is labelled for what it is.
   */
  startedAt: string | null;
  total: number;
  succeeded: number;
  failed: number;
}) {
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-4 rounded-lg border border-[color:var(--ts-border-soft)] px-5 py-4',
        'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
      )}
    >
      <Fact label="File name" value={fileName} title={fileName} />
      <Fact label="Started" value={startedAt ? formatDateTime(startedAt) : '—'} />
      <Fact label="Total records" value={formatCount(total)} numeric />
      <Fact label="Success records" value={formatCount(succeeded)} numeric />
      <Fact label="Failed records" value={formatCount(failed)} numeric tone={failed > 0 ? 'warning' : 'plain'} />
    </dl>
  );
}

function Fact({
  label,
  value,
  title,
  numeric = false,
  tone = 'plain',
}: {
  label: string;
  value: ReactNode;
  title?: string;
  numeric?: boolean;
  tone?: 'plain' | 'warning';
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd
        title={title}
        className={cn(
          'mt-1 truncate text-base text-gray-3',
          numeric && 'tabular-nums',
          tone === 'warning' && 'font-medium text-[color:var(--ts-pill-warning-text)]',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * A full stop, drawn as one: the reason across the middle of the step, with the ways out
 * under it.
 *
 * Reserved for the cases where the wizard genuinely cannot go on - the wrong tax year, a file
 * that cannot be read, references to returns Tab Service never sent. A note tucked between
 * two panels reads as one more remark about a page that is otherwise fine, and somebody who
 * scrolls past it spends the next minute looking for a Continue button that will never
 * enable. The portal draws its own stops this way for the same reason.
 */
export function BlockedStep({
  title,
  detail,
  actions,
}: {
  title: string;
  detail: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-4 px-6 py-12 text-center" role="alert">
      <span
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--ts-primary-light)]"
      >
        <AlertCircle className="h-8 w-8 text-primary" strokeWidth={1.75} />
      </span>
      <h3 className="text-[22px] leading-7 font-semibold text-gray-3 text-balance">{title}</h3>
      <p className="max-w-[46ch] text-base text-muted-foreground">{detail}</p>
      {actions ? <div className="mt-2 flex flex-wrap items-center justify-center gap-3">{actions}</div> : null}
    </div>
  );
}
